import React, { useState } from 'react';
import PFCHeader from './PFCHeaderComponent';
import PFCFooter from './PFCFooterComponent';
import { getBaseUrl } from '../../helpers/addUserWithotPayment';
import { VALID_GENDERS, VALID_RELATIONSHIPS } from './consentJourneyConstants';
import '../../assets/css/pfc.css';

const createEmptyMember = () => ({
  name: '',
  phone: '',
  email: '',
  dob: '',
  gender: '',
  relationship: '',
});

const normalizeInitialMembers = members => {
  if (!Array.isArray(members)) return [createEmptyMember()];
  const normalized = members.map(member => ({
    name: member?.name || '',
    phone: member?.phone || '',
    email: member?.email || '',
    dob: member?.dob || '',
    gender: member?.gender || '',
    relationship: member?.relationship || '',
  }));
  return normalized.length > 0 ? normalized : [createEmptyMember()];
};

const MemberInput = ({ label, value, onChange, type, placeholder, required, maxLength }) => (
  <label className="pfc-member-field">
    <span className="pfc-field-label">{label}{required && <span className="req">*</span>}</span>
    <input
      className="pfc-member-input"
      type={type || 'text'}
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      onChange={onChange}
    />
  </label>
);

const MemberSelect = ({ label, value, onChange, options, placeholder }) => (
  <label className="pfc-member-field">
    <span className="pfc-field-label">{label}<span className="req">*</span></span>
    <select className="pfc-member-input" value={value} onChange={onChange}>
      <option value="" disabled>{placeholder}</option>
      {options.map(option => <option key={option} value={option}>{option}</option>)}
    </select>
  </label>
);

const ConsentAdditionalMembersComponent = ({ token, initialMembers, maxMembers, required, onNext, onBack }) => {
  const [members, setMembers] = useState(() => normalizeInitialMembers(initialMembers));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const updateMember = (index, field, value) => {
    setMembers(current => current.map((member, memberIndex) => memberIndex === index ? { ...member, [field]: value } : member));
    if (error) setError('');
  };

  const removeMember = index => {
    setMembers(current => current.filter((_, memberIndex) => memberIndex !== index));
    setError('');
  };

  const getCompletedMembers = () => members
    .map(member => Object.keys(member).reduce((result, key) => ({ ...result, [key]: String(member[key] || '').trim() }), {}))
    .filter(member => Object.values(member).some(Boolean));

  const validateMembers = completedMembers => {
    if (required && completedMembers.length === 0) return 'Please add at least one additional member.';
    if (completedMembers.length > maxMembers) return `You can add a maximum of ${maxMembers} additional member(s).`;

    for (let index = 0; index < completedMembers.length; index += 1) {
      const member = completedMembers[index];
      if (!member.name || !member.dob || !member.gender || !member.relationship) {
        return `Please complete all required fields for member ${index + 1}.`;
      }
      if (!/^[A-Za-z\s]+$/.test(member.name)) {
        return `Please enter a valid name for member ${index + 1}. Only alphabets and spaces are allowed.`;
      }
      if (new Date(member.dob) > new Date()) {
        return `Please enter a valid date of birth for member ${index + 1}. Date cannot be in the future.`;
      }
      if (member.phone && !/^[6-9]\d{9}$/.test(member.phone)) {
        return `Please enter a valid phone number for member ${index + 1}.`;
      }
      if (member.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(member.email)) {
        return `Please enter a valid email address for member ${index + 1}.`;
      }
    }
    return '';
  };

  const handleNext = async event => {
    event.preventDefault();
    const completedMembers = getCompletedMembers();
    const validationError = validateMembers(completedMembers);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const response = await fetch(`${getBaseUrl()}/patientswebapp/affinity/consent/member-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Affinity-Token': token,
        },
        body: JSON.stringify({ members: completedMembers }),
      });
      const data = await response.json();
      if (!response.ok || data.success !== 1 || !data.data) {
        throw new Error(data.error || 'Unable to save member details. Please try again.');
      }
      onNext(data.data);
    } catch (submitError) {
      setError(submitError.message || 'Unable to save member details. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pfc-portal">
      <PFCHeader />
      <main className="pfc-members-container">
        <div className="pfc-members-card">
          <div className="pfc-members-eyebrow">MEMBERSHIP DETAILS</div>
          <div className="pfc-members-heading">
            <div className="pfc-members-heading-icon">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                <path d="M11 2L18 5.5V10C18 14.2 15.2 18 11 19.5C6.8 18 4 14.2 4 10V5.5L11 2Z" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 10.5L10.2 12.7L14.5 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="pfc-members-heading-copy">
              <div className="pfc-members-title">Additional Members</div>
              <div className="pfc-members-subtitle">Add the family members you want to include in this membership.</div>
            </div>
            <div className="pfc-members-limit">Up to {maxMembers}</div>
          </div>

          <form onSubmit={handleNext}>
            {members.map((member, index) => (
              <section className="pfc-member-card" key={index}>
                <div className="pfc-member-card-header">
                  <div className="pfc-member-card-heading">
                    <span className="pfc-member-index">{String(index + 1).padStart(2, '0')}</span>
                    <div>
                      <div className="pfc-member-title">Member {index + 1}</div>
                      <div className="pfc-member-caption">Member information</div>
                    </div>
                  </div>
                  {members.length > 1 && (
                    <button className="pfc-member-remove" type="button" onClick={() => removeMember(index)}>
                      Remove
                    </button>
                  )}
                </div>
                <div className="pfc-member-grid">
                  <MemberInput label="Full Name" value={member.name} placeholder="Enter full name" required onChange={event => updateMember(index, 'name', event.target.value)} />
                  <MemberInput label="Date of Birth" type="date" value={member.dob} required onChange={event => updateMember(index, 'dob', event.target.value)} />
                  <MemberSelect label="Gender" value={member.gender} placeholder="Select gender" options={VALID_GENDERS} onChange={event => updateMember(index, 'gender', event.target.value)} />
                  <MemberSelect label="Relationship" value={member.relationship} placeholder="Select relationship" options={VALID_RELATIONSHIPS} onChange={event => updateMember(index, 'relationship', event.target.value)} />
                  <MemberInput label="Phone Number" value={member.phone} placeholder="Optional 10-digit number" maxLength={10} onChange={event => updateMember(index, 'phone', event.target.value.replace(/\D/g, '').slice(0, 10))} />
                  <MemberInput label="Email Address" type="email" value={member.email} placeholder="Optional email address" onChange={event => updateMember(index, 'email', event.target.value)} />
                </div>
              </section>
            ))}

            {members.length < maxMembers && (
              <button className="pfc-add-member-button" type="button" onClick={() => setMembers(current => [...current, createEmptyMember()])}>
                + Add another member
              </button>
            )}

            {error && <div className="pfc-consent-error-banner" role="alert">{error}</div>}

            <div className="pfc-form-nav-between pfc-members-actions">
              {onBack ? <button className="pfc-btn-secondary" type="button" onClick={onBack} disabled={submitting}>Back</button> : <span />}
              <button className="pfc-btn-primary" type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : 'Next: Review Details →'}
              </button>
            </div>
          </form>
        </div>
      </main>
      <PFCFooter />
    </div>
  );
};

export default ConsentAdditionalMembersComponent;
